"use client";

import { Pause, Play } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePartners } from "@/lib/hooks/usePartners";
import { Reveal, SectionHeader, useInView, usePrefersReducedMotion } from "@/shared/components/home";
import { PartnerMarqueeRow } from "./partners/PartnerMarqueeRow";
import {
  fillPartnerCopy,
  partnerDuration,
  partnerSlot,
  rotateHalf,
  toPartnerView,
} from "./partners/partnerList";
import "./partners/partners.css";

const HIGHLIGHT_BY_LOCALE: Record<string, string> = {
  az: "partnyorlar",
  en: "Partners",
  ru: "Партнеры",
};

function useNarrowScreen(): boolean {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 639px)");
    const update = () => setNarrow(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return narrow;
}

function useElementWidth(ref: RefObject<HTMLElement | null>, active: boolean): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width);
    });
    observer.observe(node);
    setWidth(node.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [ref, active]);

  return width;
}

export function Partners() {
  const t = useTranslations("partners");
  const locale = useLocale();
  const { data: partners, isLoading, error } = usePartners();
  const sectionRef = useRef<HTMLElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, { once: false, threshold: 0 });
  const reduced = usePrefersReducedMotion();
  const narrow = useNarrowScreen();
  const width = useElementWidth(rowRef, !isLoading && !error);
  const [userPaused, setUserPaused] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);

  useEffect(() => {
    const onVisibility = () => setTabHidden(document.visibilityState === "hidden");
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const views = useMemo(
    () => (Array.isArray(partners) ? partners.map(toPartnerView) : []),
    [partners],
  );
  const slot = partnerSlot(narrow);
  const copy = useMemo(
    () => fillPartnerCopy(views, width || 1240, slot),
    [views, width, slot],
  );
  const paused = userPaused || reduced || tabHidden || !inView;

  return (
    <section
      ref={sectionRef}
      className="home-section home-dotgrid w-full overflow-x-clip bg-home-cream-2 py-[72px]"
    >
      <div className="mx-auto w-full max-w-[1240px] px-8">
        <Reveal>
          <SectionHeader
            align="center"
            className="mb-9"
            eyebrow={t("subtitle")}
            title={t("title")}
            highlight={HIGHLIGHT_BY_LOCALE[locale] ?? ""}
          />
        </Reveal>

        {isLoading ? (
          <p className="text-center text-[18px] text-home-body">Loading...</p>
        ) : error ? (
          <p className="text-center text-[18px] text-home-error">Error loading Partners</p>
        ) : (
          <>
            <PartnerMarqueeRow
              ref={rowRef}
              items={copy}
              duration={partnerDuration(40, narrow)}
              paused={paused}
              linkLabel={(name) => t("linkLabel", { name })}
            />
            <PartnerMarqueeRow
              items={rotateHalf(copy)}
              duration={partnerDuration(52, narrow)}
              reverse
              paused={paused}
              linkLabel={(name) => t("linkLabel", { name })}
            />
            {copy.length > 0 ? (
            <div className="partner-controls mt-6 flex flex-col items-center gap-3">
              <button
                type="button"
                aria-pressed={userPaused}
                aria-label={userPaused ? t("play") : t("pause")}
                onClick={() => setUserPaused((value) => !value)}
                className={
                  userPaused
                    ? "inline-flex h-11 w-11 items-center justify-center rounded-full border-[1.5px] border-home-navy bg-home-navy text-home-cream"
                    : "inline-flex h-11 w-11 items-center justify-center rounded-full border-[1.5px] border-home-navy bg-home-cream text-home-navy"
                }
              >
                {userPaused ? (
                  <Play className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <Pause className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
              <p className="max-w-md text-center text-[16px] leading-snug text-home-body">{t("hint")}</p>
                      </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
